import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  Video, Plus, Eye, Share2, Trash2, Copy, ExternalLink,
  Crown, AlertCircle, Loader2, Calendar, Clock, Building2, User,
  CreditCard, IndianRupee, DollarSign, CheckCircle, ArrowLeft, Settings, X,
  Gift, Link2, MessageCircle, Mail, Check, Users, Download, Bell,
  BarChart3, TrendingUp, Youtube, Image, CalendarDays, FileDown, ChevronDown, ChevronUp
} from 'lucide-react';
import BackButton from '../components/BackButton';
import { UpgradeNudge } from '../components/UpgradeNudge';
import { PaymentTermsCheck } from '../components/PaymentTermsCheck';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Load Razorpay script
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

function ProfilePerformance() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [days, setDays] = React.useState(30);

  React.useEffect(() => {
    setLoading(true);
    axios.get(`${API_URL}/api/analytics/my-profiles?days=${days}`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [days]);

  if (loading) return (
    <div className="mb-6 bg-zinc-900 border border-zinc-800 rounded-xl p-6 animate-pulse">
      <div className="h-5 bg-zinc-800 rounded w-48 mb-4" />
      <div className="grid grid-cols-4 gap-3">
        {[...Array(4)].map((_, i) => <div key={i} className="h-16 bg-zinc-800 rounded-lg" />)}
      </div>
    </div>
  );

  if (!data || !data.profiles || data.profiles.length === 0) return null;

  // Aggregate across all profiles
  const totals = data.profiles.reduce((acc, p) => ({
    page_views: acc.page_views + (p.page_views || 0),
    unique_visitors: acc.unique_visitors + (p.unique_visitors || 0),
    video_plays: acc.video_plays + (p.video_plays || 0),
    photo_views: acc.photo_views + (p.photo_views || 0),
    youtube_views: acc.youtube_views + (p.youtube_views || 0),
  }), { page_views: 0, unique_visitors: 0, video_plays: 0, photo_views: 0, youtube_views: 0 });

  // Build sparkline from daily_views
  const allDays = {};
  data.profiles.forEach(p => {
    Object.entries(p.daily_views || {}).forEach(([d, c]) => {
      allDays[d] = (allDays[d] || 0) + c;
    });
  });
  const sortedDays = Object.entries(allDays).sort((a, b) => a[0].localeCompare(b[0]));
  const maxVal = Math.max(...sortedDays.map(d => d[1]), 1);

  return (
    <div className="mb-6 bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden" data-testid="profile-performance">
      <div className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-white flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#C41E3A]" />
            Profile Performance
          </h3>
          <div className="flex gap-1">
            {[7, 30, 90].map(d => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${days === d ? 'bg-[#C41E3A] text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`}
                data-testid={`analytics-period-${d}`}
              >
                {d}d
              </button>
            ))}
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
            <Eye className="w-4 h-4 text-blue-400 mx-auto mb-1" />
            <p className="text-xl font-bold text-white" data-testid="analytics-page-views">{totals.page_views}</p>
            <p className="text-[10px] text-zinc-500">Profile Views</p>
          </div>
          <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
            <Users className="w-4 h-4 text-emerald-400 mx-auto mb-1" />
            <p className="text-xl font-bold text-emerald-400">{totals.unique_visitors}</p>
            <p className="text-[10px] text-zinc-500">Unique Visitors</p>
          </div>
          <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
            <Video className="w-4 h-4 text-purple-400 mx-auto mb-1" />
            <p className="text-xl font-bold text-purple-400">{totals.video_plays}</p>
            <p className="text-[10px] text-zinc-500">Video Plays</p>
          </div>
          <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
            <Image className="w-4 h-4 text-amber-400 mx-auto mb-1" />
            <p className="text-xl font-bold text-amber-400">{totals.photo_views}</p>
            <p className="text-[10px] text-zinc-500">Photo Views</p>
          </div>
          <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
            <Youtube className="w-4 h-4 text-red-500 mx-auto mb-1" />
            <p className="text-xl font-bold text-red-400" data-testid="analytics-youtube-views">{totals.youtube_views.toLocaleString()}</p>
            <p className="text-[10px] text-zinc-500">YouTube Views</p>
          </div>
        </div>

        {/* Mini Sparkline Chart */}
        {sortedDays.length > 1 && (
          <div className="bg-zinc-800/30 rounded-lg p-3">
            <div className="flex items-center gap-1.5 mb-2">
              <TrendingUp className="w-3.5 h-3.5 text-zinc-500" />
              <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Daily Profile Views</span>
            </div>
            <div className="flex items-end gap-px h-10">
              {sortedDays.map(([day, count]) => (
                <div
                  key={day}
                  className="flex-1 bg-[#C41E3A]/60 hover:bg-[#C41E3A] rounded-t transition-colors relative group"
                  style={{ height: `${Math.max((count / maxVal) * 100, 4)}%` }}
                  title={`${day}: ${count} views`}
                >
                  <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-zinc-900 text-white text-[9px] px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-10">
                    {day.slice(5)}: {count}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* YouTube Video Breakdown */}
        {data.profiles.some(p => Object.keys(p.youtube_videos || {}).length > 0) && (
          <div className="mt-4 pt-4 border-t border-zinc-800">
            <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <Youtube className="w-3.5 h-3.5 text-red-500" />
              YouTube Video Views
            </p>
            <div className="space-y-1.5">
              {data.profiles.flatMap(p => 
                Object.entries(p.youtube_videos || {}).map(([vid, stats]) => ({
                  vid, views: stats.views, likes: stats.likes, profile: p.company_name
                }))
              ).sort((a, b) => b.views - a.views).slice(0, 5).map(v => (
                <div key={v.vid} className="flex items-center justify-between text-xs">
                  <a href={`https://youtube.com/watch?v=${v.vid}`} target="_blank" rel="noopener noreferrer" className="text-zinc-300 hover:text-white truncate max-w-[200px]" data-testid={`yt-video-${v.vid}`}>
                    {v.vid}
                  </a>
                  <div className="flex items-center gap-3 text-zinc-400">
                    <span>{v.views.toLocaleString()} views</span>
                    <span>{v.likes.toLocaleString()} likes</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PartnerDashboard() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [exporting, setExporting] = React.useState(false);

  React.useEffect(() => {
    axios.get(`${API_URL}/api/referral/partner-stats`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data || !data.is_partner) return null;

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API_URL}/api/referral/partner-export`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `partner_referrals_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      alert('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="mb-8 bg-zinc-900 border border-amber-500/20 rounded-xl overflow-hidden" data-testid="partner-dashboard">
      <div className="p-6">
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-amber-400" />
              Partner Dashboard
              <span className="bg-amber-500/20 text-amber-400 text-xs px-2 py-0.5 rounded-full font-medium">Partner</span>
            </h3>
            <p className="text-zinc-400 text-sm mt-1">
              Track all your referrals, credits, and activity as a BrandStage Partner.
            </p>
          </div>
          <button
            onClick={handleExportCSV}
            disabled={exporting}
            className="flex items-center gap-1.5 bg-zinc-800 hover:bg-zinc-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors border border-zinc-700 disabled:opacity-50"
            data-testid="partner-export-csv-btn"
          >
            <Download className="w-4 h-4" />
            {exporting ? 'Exporting...' : 'Export CSV'}
          </button>
        </div>

        {/* Partner Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
            <p className="text-xs text-zinc-500 mb-1">Total Referrals</p>
            <p className="text-xl font-bold text-white" data-testid="partner-total-referrals">{data.stats.total_referrals}</p>
          </div>
          <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
            <p className="text-xs text-zinc-500 mb-1">Successful</p>
            <p className="text-xl font-bold text-emerald-400">{data.stats.successful}</p>
          </div>
          <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
            <p className="text-xs text-zinc-500 mb-1">Total Earned</p>
            <p className="text-xl font-bold text-amber-400">₹{(data.stats.total_earned || 0).toLocaleString()}</p>
          </div>
          <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
            <p className="text-xs text-zinc-500 mb-1">Credit Balance</p>
            <p className="text-xl font-bold text-emerald-400" data-testid="partner-credit-balance">₹{(data.stats.credit_balance || 0).toLocaleString()}</p>
          </div>
        </div>

        {/* Cash Payout Note */}
        {data.stats.credit_balance >= 5000 && (
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 mb-5 flex items-start gap-3" data-testid="partner-cash-payout-note">
            <IndianRupee className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="text-amber-300 font-medium">Cash Payout Available</p>
              <p className="text-zinc-400 mt-0.5">Your credit balance is ₹{data.stats.credit_balance.toLocaleString()} — you're eligible for a cash payout. Contact <a href="mailto:operations@brandstage-media.com" className="text-amber-400 hover:underline">operations@brandstage-media.com</a> to request a bank transfer.</p>
            </div>
          </div>
        )}

        {/* Referral Table */}
        {data.referrals.length > 0 ? (
          <div className="border border-zinc-800 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="partner-referrals-table">
                <thead className="bg-zinc-800/50 text-zinc-400">
                  <tr>
                    <th className="px-4 py-3 text-left">Contact Name</th>
                    <th className="px-4 py-3 text-left">Email</th>
                    <th className="px-4 py-3 text-left">Subscription</th>
                    <th className="px-4 py-3 text-right">Credit Earned</th>
                    <th className="px-4 py-3 text-left">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800">
                  {data.referrals.map(r => (
                    <tr key={r.id} className="text-zinc-300">
                      <td className="px-4 py-3 text-white font-medium">{r.referee_name || '—'}</td>
                      <td className="px-4 py-3 text-zinc-400">{r.referee_email}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${r.subscription_status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-500/20 text-zinc-400'}`}>
                          {r.subscription_status === 'active' ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-amber-400">₹{r.credit_amount}</td>
                      <td className="px-4 py-3 text-zinc-500 text-xs">{r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="text-center py-6 text-zinc-500 text-sm">
            No referrals yet. Share your referral link above to start earning.
          </div>
        )}

        {/* Link to Partner Guide */}
        <div className="mt-4 pt-4 border-t border-zinc-800 flex items-center justify-between text-xs text-zinc-500">
          <span className="flex items-center gap-1.5">
            <Gift className="w-3.5 h-3.5" />
            Partners earn up to ₹2,000 per annual referral
          </span>
          <a href="/partner-programme" className="text-amber-400 hover:underline" data-testid="partner-guide-link">Partner Programme Guide →</a>
        </div>
      </div>
    </div>
  );
}


function RSVPDashboard() {
  const [invitations, setInvitations] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [expandedId, setExpandedId] = React.useState(null);

  React.useEffect(() => {
    const fetch = async () => {
      try {
        // Try user's own profiles first, then admin endpoint
        let profiles = [];
        try {
          const res = await axios.get(`${API_URL}/api/profiles/mine`, { withCredentials: true });
          profiles = (res.data.profiles || []).map(p => ({ ...p, id: p.id || p._id }));
        } catch {}
        // If admin with no personal profiles, try admin endpoint
        if (profiles.length === 0) {
          try {
            const adminRes = await axios.get(`${API_URL}/api/admin/profiles`, { withCredentials: true });
            const adminProfiles = adminRes.data;
            profiles = (Array.isArray(adminProfiles) ? adminProfiles : adminProfiles.profiles || []).map(p => ({ ...p, id: p.id || p._id }));
          } catch {}
        }

        const allInvitations = [];
        for (const p of profiles) {
          const pid = p.id;
          if (!pid) continue;
          try {
            const invRes = await axios.get(`${API_URL}/api/profiles/${pid}/invitations`, { withCredentials: true });
            for (const inv of (invRes.data.invitations || [])) {
              allInvitations.push({ ...inv, profile_name: p.company_name || p.leader_name || '', profile_id: pid });
            }
          } catch {}
          // Also fetch showcase opportunities
          try {
            const oppRes = await axios.get(`${API_URL}/api/profiles/${pid}/showcase-opportunities`, { withCredentials: true });
            for (const opp of oppRes.data) {
              allInvitations.push({
                ...opp,
                event_title: opp.title,
                is_opportunity: true,
                profile_name: opp.profile_name || p.company_name || '',
                profile_id: pid
              });
            }
          } catch {}
        }

        setInvitations(allInvitations);
      } catch {}
      setLoading(false);
    };
    fetch();
  }, []);

  const exportCSV = (inv) => {
    const responses = inv.rsvps || inv.responses || [];
    if (!responses.length) return;
    const headers = ['Name', 'Company', 'Email', 'Phone', 'Designation', 'Message', 'RSVP Status', 'Date'];
    const rows = responses.map(r => [
      r.name, r.company || '', r.email, r.phone || '', r.designation || '',
      (r.message || '').replace(/,/g, ';'), r.rsvp_status || 'interested',
      new Date(r.created_at).toLocaleDateString()
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rsvp_${(inv.event_title || inv.title || 'export').replace(/\s+/g, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return null;
  if (invitations.length === 0) return null;

  const totalInterested = invitations.reduce((s, inv) => {
    const resps = inv.rsvps || inv.responses || [];
    return s + resps.filter(r => (r.rsvp_status || 'interested') === 'interested').length;
  }, 0);
  const totalDeclined = invitations.reduce((s, inv) => {
    const resps = inv.rsvps || inv.responses || [];
    return s + resps.filter(r => r.rsvp_status === 'declined').length;
  }, 0);
  const totalResponses = totalInterested + totalDeclined;

  return (
    <div className="mb-8" data-testid="rsvp-dashboard">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold flex items-center gap-2">
          <CalendarDays className="w-5 h-5 text-[#C41E3A]" />
          RSVP Dashboard
        </h2>
        <div className="flex items-center gap-3 text-sm">
          <span className="flex items-center gap-1.5 text-emerald-400">
            <CheckCircle className="w-4 h-4" /> {totalInterested} interested
          </span>
          <span className="flex items-center gap-1.5 text-red-400">
            <X className="w-4 h-4" /> {totalDeclined} declined
          </span>
          <span className="text-zinc-500">{totalResponses} total</span>
        </div>
      </div>

      <div className="space-y-3">
        {invitations.map(inv => {
          const responses = inv.rsvps || inv.responses || [];
          const interested = responses.filter(r => (r.rsvp_status || 'interested') === 'interested').length;
          const declined = responses.filter(r => r.rsvp_status === 'declined').length;
          const isExpanded = expandedId === (inv.id || inv._id);
          const invId = inv.id || inv._id;

          return (
            <div key={invId} className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden" data-testid={`rsvp-inv-${invId}`}>
              <div className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {inv.is_opportunity ? (
                        <span className="text-[10px] px-2 py-0.5 rounded bg-[#C41E3A]/10 text-[#C41E3A] font-bold">OPPORTUNITY</span>
                      ) : (
                        <span className="text-[10px] px-2 py-0.5 rounded bg-blue-500/10 text-blue-400 font-bold">EVENT</span>
                      )}
                      <span className={`text-[10px] px-2 py-0.5 rounded font-bold uppercase ${
                        inv.status === 'published' ? 'bg-emerald-500/20 text-emerald-400' :
                        inv.status === 'closed' ? 'bg-red-500/20 text-red-400' : 'bg-zinc-700 text-zinc-400'
                      }`}>
                        {inv.status || 'active'}
                      </span>
                    </div>
                    <h3 className="text-white font-semibold text-sm">{inv.event_title || inv.title}</h3>
                    <div className="flex flex-wrap items-center gap-3 mt-1 text-[11px] text-zinc-500">
                      {inv.venue && <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{inv.venue}</span>}
                      {inv.location && <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{inv.location}</span>}
                      {inv.event_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(inv.event_date).toLocaleDateString()}</span>}
                    </div>
                  </div>

                  <div className="flex items-center gap-3 shrink-0">
                    {/* Counts */}
                    <div className="flex items-center gap-2 text-xs">
                      <span className="bg-emerald-500/10 text-emerald-400 px-2 py-1 rounded font-bold">{interested}</span>
                      <span className="bg-red-500/10 text-red-400 px-2 py-1 rounded font-bold">{declined}</span>
                    </div>
                    {responses.length > 0 && (
                      <button
                        onClick={() => exportCSV(inv)}
                        className="p-1.5 rounded hover:bg-zinc-800 transition-colors" title="Export CSV"
                        data-testid={`export-csv-${invId}`}
                      >
                        <FileDown className="w-4 h-4 text-zinc-400" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Toggle responses */}
                {responses.length > 0 && (
                  <button
                    onClick={() => setExpandedId(isExpanded ? null : invId)}
                    className="flex items-center gap-1 mt-2 text-xs text-[#C41E3A] hover:underline"
                    data-testid={`toggle-rsvps-${invId}`}
                  >
                    {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    {responses.length} response{responses.length > 1 ? 's' : ''}
                  </button>
                )}
                {responses.length === 0 && (
                  <p className="text-zinc-600 text-xs mt-2">No responses yet</p>
                )}
              </div>

              {/* Expanded responses table */}
              {isExpanded && (
                <div className="border-t border-zinc-800 bg-zinc-950/50 px-4 py-3">
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-zinc-500 border-b border-zinc-800">
                          <th className="text-left py-2 pr-3 font-medium">Name</th>
                          <th className="text-left py-2 pr-3 font-medium">Company</th>
                          <th className="text-left py-2 pr-3 font-medium">Email</th>
                          <th className="text-left py-2 pr-3 font-medium">RSVP</th>
                          <th className="text-left py-2 font-medium">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {responses.map((r, i) => (
                          <tr key={r.id || i} className="border-b border-zinc-800/50 last:border-0">
                            <td className="py-2 pr-3 text-white font-medium">{r.name}</td>
                            <td className="py-2 pr-3 text-zinc-400">{r.company || '—'}</td>
                            <td className="py-2 pr-3 text-zinc-400">{r.email}</td>
                            <td className="py-2 pr-3">
                              <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${
                                r.rsvp_status === 'declined' ? 'bg-red-500/20 text-red-400' : 'bg-emerald-500/20 text-emerald-400'
                              }`}>
                                {r.rsvp_status === 'declined' ? 'Declined' : 'Interested'}
                              </span>
                            </td>
                            <td className="py-2 text-zinc-500">{new Date(r.created_at).toLocaleDateString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


function NotificationBanner() {
  const [notifications, setNotifications] = React.useState([]);

  React.useEffect(() => {
    axios.get(`${API_URL}/api/notifications`, { withCredentials: true })
      .then(r => setNotifications(r.data.notifications || []))
      .catch(() => {});
  }, []);

  const dismiss = async (id) => {
    try {
      await axios.post(`${API_URL}/api/notifications/${id}/dismiss`, {}, { withCredentials: true });
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch {}
  };

  const dismissAll = async () => {
    try {
      await axios.post(`${API_URL}/api/notifications/dismiss-all`, {}, { withCredentials: true });
      setNotifications([]);
    } catch {}
  };

  if (notifications.length === 0) return null;

  return (
    <div className="mb-6 space-y-2" data-testid="notification-banner">
      {notifications.map(n => (
        <div key={n.id} className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 flex items-start gap-3" data-testid={`notification-${n.id}`}>
          <Bell className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-white font-semibold text-sm">{n.subject}</p>
            <p className="text-zinc-300 text-sm mt-1 whitespace-pre-line leading-relaxed">{n.message}</p>
            <p className="text-zinc-500 text-xs mt-2">{n.created_at ? new Date(n.created_at).toLocaleDateString() : ''}</p>
          </div>
          <button onClick={() => dismiss(n.id)} className="text-zinc-500 hover:text-white transition-colors shrink-0" data-testid={`dismiss-notification-${n.id}`}>
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}
      {notifications.length > 1 && (
        <button onClick={dismissAll} className="text-xs text-zinc-500 hover:text-white transition-colors ml-1" data-testid="dismiss-all-notifications">
          Dismiss all
        </button>
      )}
    </div>
  );
}

function ReferEarnCard() {
  const [data, setData] = React.useState(null);
  const [copied, setCopied] = React.useState(false);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    axios.get(`${API_URL}/api/referral/my`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data) return null;

  const copyLink = () => {
    navigator.clipboard.writeText(data.referral_link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareWhatsApp = () => {
    const text = `Check out BrandStage — a video-first professional profile platform for businesses. Use my referral link and get ₹250 off your first subscription!\n\n${data.referral_link}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const shareEmail = () => {
    const subject = 'Try BrandStage — Video-First Business Profiles';
    const body = `Hi,\n\nI've been using BrandStage to create a professional video-first business profile. I think you'd find it valuable too.\n\nUse my referral link and get ₹250 off your first subscription:\n${data.referral_link}\n\nBest regards`;
    window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_blank');
  };

  return (
    <div className="mb-8 bg-gradient-to-r from-zinc-900 to-zinc-900/80 border border-zinc-800 rounded-xl overflow-hidden" data-testid="refer-earn-card">
      <div className="p-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <Gift className="w-5 h-5 text-[#C41E3A]" />
              Refer & Earn Credits
            </h3>
            <p className="text-zinc-400 text-sm mt-1">
              Share BrandStage with your network. Earn up to <span className="text-white font-medium">₹1,000</span> in credits per referral.
            </p>
          </div>
          {data.credit_balance > 0 && (
            <div className="text-right shrink-0" data-testid="credit-balance">
              <p className="text-xs text-zinc-500 uppercase tracking-wide">Your Credits</p>
              <p className="text-2xl font-bold text-emerald-400">₹{data.credit_balance.toLocaleString()}</p>
              <p className="text-xs text-zinc-500">Auto-applied at checkout</p>
            </div>
          )}
        </div>

        {/* Referral link + share buttons */}
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="flex-1 flex items-center gap-2 bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2.5">
            <Link2 className="w-4 h-4 text-zinc-500 shrink-0" />
            <span className="text-sm text-zinc-300 truncate" data-testid="referral-link-text">{data.referral_link}</span>
          </div>
          <div className="flex gap-2 shrink-0">
            <button onClick={copyLink} className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${copied ? 'bg-emerald-600 text-white' : 'bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700'}`} data-testid="copy-referral-btn">
              {copied ? <><Check className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy</>}
            </button>
            <button onClick={shareWhatsApp} className="px-3 py-2.5 rounded-lg bg-[#25D366]/20 hover:bg-[#25D366]/30 text-[#25D366] transition-colors" title="Share on WhatsApp" data-testid="share-whatsapp-btn">
              <MessageCircle className="w-4 h-4" />
            </button>
            <button onClick={shareEmail} className="px-3 py-2.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700 transition-colors" title="Share via Email" data-testid="share-email-btn">
              <Mail className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Reward tiers */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
            <p className="text-xs text-zinc-500 mb-1">6-Month Plan</p>
            <p className="text-lg font-bold text-white">₹250</p>
          </div>
          <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
            <p className="text-xs text-zinc-500 mb-1">1-Year Plan</p>
            <p className="text-lg font-bold text-white">₹500</p>
          </div>
          <div className="bg-zinc-800/50 rounded-lg p-3 text-center border border-[#C41E3A]/30">
            <p className="text-xs text-zinc-500 mb-1">All-Access</p>
            <p className="text-lg font-bold text-[#C41E3A]">₹1,000</p>
          </div>
        </div>

        {/* Referral stats */}
        <div className="flex items-center gap-6 text-sm text-zinc-400">
          <span data-testid="referral-count">{data.referrals.length} referral{data.referrals.length !== 1 ? 's' : ''}</span>
          <span>₹{data.total_earned.toLocaleString()} earned</span>
          <span>₹{data.total_redeemed.toLocaleString()} redeemed</span>
        </div>

        {/* Their referred person also gets a benefit */}
        <div className="mt-4 pt-4 border-t border-zinc-800 flex items-center justify-between text-xs text-zinc-500">
          <div className="flex items-center gap-2">
            <Users className="w-3.5 h-3.5" />
            Your referral gets <span className="text-white font-medium">₹250 off</span> their first subscription. Credits expire at end of your subscription period.
          </div>
          <a href="/referral-programme" className="text-[#C41E3A] hover:underline shrink-0 ml-2" data-testid="referral-guide-link">Learn more →</a>
        </div>
      </div>
    </div>
  );
}

function MyProfiles() {
  const [companyProfiles, setCompanyProfiles] = React.useState([]);
  const [leadershipProfiles, setLeadershipProfiles] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchAll = async () => {
      try {
        const [compRes, leadRes] = await Promise.all([
          axios.get(`${API_URL}/api/profiles/mine`, { withCredentials: true }).catch(() => ({ data: { profiles: [] } })),
          axios.get(`${API_URL}/api/my-leadership-profiles`, { withCredentials: true }).catch(() => ({ data: [] }))
        ]);
        setCompanyProfiles(compRes.data.profiles || []);
        setLeadershipProfiles(Array.isArray(leadRes.data) ? leadRes.data : []);
      } catch {}
      setLoading(false);
    };
    fetchAll();
  }, []);

  const all = [
    ...companyProfiles.map(p => ({ ...p, type: 'company', editUrl: `/profile/edit/${p.id}`, viewUrl: `/p/${p.slug}` })),
    ...leadershipProfiles.map(p => ({ ...p, type: 'leadership', editUrl: `/leadership/edit/${p.id}`, viewUrl: `/leader/${p.slug}` }))
  ];

  if (loading) return (
    <div className="mb-8 flex items-center gap-2 text-zinc-500 text-sm">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading profiles…
    </div>
  );

  if (all.length === 0) return null;

  return (
    <div className="mb-8" data-testid="my-profiles-section">
      <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
        <Building2 className="w-5 h-5 text-[#C41E3A]" />
        My Profiles
      </h2>
      <div className="grid md:grid-cols-2 gap-4">
        {all.map(p => {
          const name = p.company_name || p.leader_name || 'Untitled';
          const isLeadership = p.type === 'leadership';
          const isActive = p.subscription_status === 'active' || p.active_until;
          return (
            <div key={p.id} className="bg-zinc-900 border border-zinc-800 rounded-lg p-5 flex items-center gap-4 group hover:border-zinc-700 transition-colors" data-testid={`profile-card-${p.id}`}>
              {p.logo_url ? (
                <img src={p.logo_url} alt="" className="w-12 h-12 rounded-lg object-cover bg-zinc-800" />
              ) : (
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${isLeadership ? 'bg-purple-500/20' : 'bg-[#C41E3A]/20'}`}>
                  {isLeadership
                    ? <User className="w-6 h-6 text-purple-400" />
                    : <Building2 className="w-6 h-6 text-[#C41E3A]" />
                  }
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold truncate">{name}</h3>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${isLeadership ? 'bg-purple-500/20 text-purple-400' : 'bg-zinc-800 text-zinc-400'}`}>
                    {isLeadership ? 'Leadership' : 'Company'}
                  </span>
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${isActive ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>
                    {isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Link to={p.viewUrl} target="_blank" className="w-8 h-8 rounded flex items-center justify-center text-zinc-500 hover:text-white hover:bg-zinc-800 transition-colors" title="View public page">
                  <ExternalLink className="w-4 h-4" />
                </Link>
                <Link to={p.editUrl} className="px-3 py-1.5 text-xs font-semibold rounded bg-zinc-800 text-white hover:bg-[#C41E3A] transition-colors" data-testid={`edit-profile-${p.id}`}>
                  Edit
                </Link>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, loading: authLoading, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [upgradeOffer, setUpgradeOffer] = useState(null);
  const [access, setAccess] = useState(null);
  const [paymentTermsAccepted, setPaymentTermsAccepted] = useState(false);
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);

  useEffect(() => {
    if (authLoading) return; // Wait for auth check to complete
    if (!user) {
      navigate('/login');
      return;
    }
    fetchVideos();
    fetchPaymentData();
  }, [user, authLoading, navigate]);

  const fetchVideos = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/videos/my-videos`, {
        withCredentials: true
      });
      setVideos(response.data);
    } catch (error) {
      console.error('Failed to fetch videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentData = async () => {
    try {
      const [pendingRes, historyRes] = await Promise.all([
        axios.get(`${API_URL}/api/payments/pending-requests`, { withCredentials: true }).catch(() => ({ data: { payment_requests: [] } })),
        axios.get(`${API_URL}/api/payments/my-orders`, { withCredentials: true }).catch(() => ({ data: { orders: [] } }))
      ]);
      setPendingPayments(pendingRes.data.payment_requests || []);
      setPaymentHistory(historyRes.data.orders || []);
      axios.get(`${API_URL}/api/me/addons`, { withCredentials: true }).then((r) => { setUpgradeOffer(r.data.upgrade_offer); setAccess(r.data); }).catch(() => {});
    } catch (error) {
      console.error('Failed to fetch payment data:', error);
    }
  };

  // Handle Razorpay payment
  const handlePayment = async (payment, currency) => {
    if (!paymentTermsAccepted) {
      alert('Please accept the plan terms before paying.');
      return;
    }
    const scriptLoaded = await loadRazorpayScript();
    if (!scriptLoaded) {
      alert('Failed to load payment gateway. Please try again.');
      return;
    }

    try {
      // Determine amount based on currency
      const amount = currency === 'USD' ? payment.amount_usd : payment.amount_inr;
      const amountWithGst = currency === 'USD' ? amount : Math.round(amount * 1.18);
      
      // Create order on backend
      const orderRes = await axios.post(`${API_URL}/api/payments/create-order`, {
        product_type: 'custom',
        terms_accepted: true,
        country: currency === 'USD' ? 'international' : 'india',
        payment_request_id: payment.id
      }, { withCredentials: true });

      const { order_id, razorpay_key_id, amount: orderAmount, currency: orderCurrency } = orderRes.data;

      // Open Razorpay checkout
      const options = {
        key: razorpay_key_id,
        amount: orderAmount,
        currency: orderCurrency,
        name: 'BrandStage',
        description: payment.description,
        order_id: order_id,
        handler: async function (response) {
          // Verify payment on backend
          try {
            await axios.post(`${API_URL}/api/payments/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            }, { withCredentials: true });
            
            alert('Payment successful! Thank you.');
            fetchPaymentData(); // Refresh payment data
          } catch (err) {
            alert('Payment verification failed. Please contact support.');
          }
        },
        prefill: {
          email: user?.email || '',
          name: user?.name || ''
        },
        theme: {
          color: '#C41E3A'
        },
        modal: {
          ondismiss: function() {
            console.log('Payment modal closed');
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      console.error('Payment error:', error);
      alert(error.response?.data?.detail || 'Failed to initiate payment. Please try again.');
    }
  };

  const handleDelete = async (videoId) => {
    if (!window.confirm('Are you sure you want to delete this video?')) return;
    
    setDeleting(videoId);
    try {
      await axios.delete(`${API_URL}/api/videos/${videoId}`, {
        withCredentials: true
      });
      setVideos(videos.filter(v => v.id !== videoId));
      refreshUser();
    } catch (error) {
      console.error('Failed to delete video:', error);
      alert('Failed to delete video');
    } finally {
      setDeleting(null);
    }
  };

  const copyShareLink = (shareId) => {
    const shareUrl = `${window.location.origin}/v/${shareId}`;
    navigator.clipboard.writeText(shareUrl);
    alert('Link copied to clipboard!');
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== 'DELETE') {
      alert('Please type DELETE to confirm');
      return;
    }
    
    setDeletingAccount(true);
    try {
      await axios.post(`${API_URL}/api/account/delete`, {
        confirmation: 'DELETE',
        reason: deleteReason || 'No reason provided'
      }, { withCredentials: true });
      
      alert('Your account has been deleted. You will now be logged out.');
      // Clear auth and redirect
      window.location.href = '/';
    } catch (error) {
      console.error('Failed to delete account:', error);
      alert(error.response?.data?.detail || 'Failed to delete account. Please contact support.');
    } finally {
      setDeletingAccount(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-24 pb-16 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
      </div>
    );
  }

  if (!user) return null;

  const tierColors = {
    free: 'bg-zinc-700',
    pro: 'bg-blue-600',
    business: 'bg-purple-600'
  };

  return (
    <div className="min-h-screen bg-zinc-950 pt-24 pb-16" data-testid="dashboard">
      <div className="max-w-6xl mx-auto px-6">
        {/* Back Button */}
        <div className="mb-6">
          <BackButton to="/" label="Back to Home" />
        </div>
        
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-black tracking-tight mb-2">Dashboard</h1>
            <p className="text-zinc-400">Manage your videos and subscription · <Link to="/account/privacy" className="text-zinc-500 hover:text-white underline" data-testid="privacy-consents-link">Privacy & consents</Link></p>
          </div>
          <Link 
            to="/studio"
            className="btn-primary rounded-sm flex items-center gap-2 justify-center"
            data-testid="new-video-btn"
          >
            <Plus className="w-5 h-5" />
            Record New Video
          </Link>
        </div>

        {/* Stats Cards */}
        {/* Admin Quick Access */}
        {user?.email?.toLowerCase() === 'brandstagevideo@gmail.com' && (
          <Link 
            to="/admin"
            className="flex items-center gap-3 mb-6 bg-zinc-900 border border-zinc-800 hover:border-[#C41E3A]/50 rounded-lg px-5 py-3 transition-colors group"
            data-testid="admin-dashboard-link"
          >
            <Settings className="w-5 h-5 text-[#C41E3A]" />
            <div className="flex-1">
              <span className="text-white font-semibold text-sm">Admin Dashboard</span>
              <span className="text-zinc-500 text-xs ml-2">Manage profiles, reviews, payments & more</span>
            </div>
            <ExternalLink className="w-4 h-4 text-zinc-600 group-hover:text-[#C41E3A] transition-colors" />
          </Link>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Subscription */}
          <div className="bg-zinc-900 border border-zinc-800 p-6" data-testid="subscription-card">
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs uppercase tracking-wider text-zinc-500">Subscription</span>
              <span className={`${access?.plan_tier ? 'bg-emerald-500/20 text-emerald-400' : tierColors.free} px-2 py-1 text-xs font-bold uppercase rounded-sm`} data-testid="plan-tier-badge">
                {access?.plan_tier === 'all_access' ? 'All-Access' : access?.plan_tier === 'basic' ? 'Basic' : 'Inactive'}
              </span>
            </div>
            <div className="flex items-center gap-2 mb-2">
              <Crown className="w-5 h-5 text-[#C41E3A]" />
              <span className="text-xl font-bold" data-testid="plan-tier-label">
                {access?.plan_tier === 'all_access' ? 'All-Access Plan' : access?.plan_tier === 'basic' ? 'Basic Plan' : 'No active plan'}
              </span>
            </div>
            {access?.plan_tier === 'basic' && access.active_addons?.length > 0 && (
              <p className="text-xs text-zinc-500 mb-2" data-testid="plan-addons-count">+ {access.active_addons.length} Pro feature{access.active_addons.length > 1 ? 's' : ''}</p>
            )}
            {access?.plan_tier !== 'all_access' && (
              <Link 
                to="/pricing" 
                className="text-sm text-[#C41E3A] hover:underline"
                data-testid="upgrade-link"
              >
                {access?.plan_tier === 'basic' ? 'Add Pro features →' : 'View plans →'}
              </Link>
            )}
          </div>

          {/* Videos Used */}
          <div className="bg-zinc-900 border border-zinc-800 p-6" data-testid="videos-card">
            <span className="text-xs uppercase tracking-wider text-zinc-500 block mb-4">Videos This Month</span>
            <div className="flex items-end gap-2 mb-2">
              <span className="text-4xl font-black">{user.videos_count}</span>
              <span className="text-zinc-500 mb-1">/ {user.videos_limit === 999 ? '∞' : user.videos_limit}</span>
            </div>
            <div className="w-full h-2 bg-zinc-800 rounded-full overflow-hidden">
              <div 
                className="h-full bg-[#C41E3A] transition-all"
                style={{ width: `${Math.min((user.videos_count / user.videos_limit) * 100, 100)}%` }}
              />
            </div>
          </div>

          {/* Total Views */}
          <div className="bg-zinc-900 border border-zinc-800 p-6" data-testid="views-card">
            <span className="text-xs uppercase tracking-wider text-zinc-500 block mb-4">Total Views</span>
            <div className="flex items-center gap-2">
              <Eye className="w-6 h-6 text-zinc-500" />
              <span className="text-4xl font-black">
                {videos.reduce((sum, v) => sum + (v.views || 0), 0)}
              </span>
            </div>
          </div>
        </div>

        {/* ── Broadcast Notifications ──────────────────────────────── */}
        <NotificationBanner />

        {/* ── Partner Dashboard (visible only for partners) ──────── */}
        <PartnerDashboard />

        {/* ── RSVP Dashboard ─────────────────────────────────────── */}
        <RSVPDashboard />

        {upgradeOffer?.eligible && (
          <div className="mb-8 max-w-xl">
            <UpgradeNudge currentValue={upgradeOffer.current_value_inr} allAccessPrice={upgradeOffer.all_access_inr} testId="dashboard-upgrade-nudge" />
          </div>
        )}

        {/* Pending Payment Requests */}
        {pendingPayments.length > 0 && (
          <div className="mb-8" data-testid="pending-payments">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-amber-400" />
              Pending Payment Requests
            </h2>
            <div className="mb-4 max-w-2xl"><PaymentTermsCheck checked={paymentTermsAccepted} onChange={setPaymentTermsAccepted} testId="dashboard-payment-terms" /></div>
            <div className="space-y-4">
              {pendingPayments.map((payment) => (
                <div 
                  key={payment.id}
                  className="bg-zinc-900 border border-amber-500/30 p-6 rounded-lg"
                  data-testid={`pending-payment-${payment.id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <h3 className="font-bold text-lg">{payment.description}</h3>
                      <p className="text-zinc-400 text-sm">
                        Sent on {new Date(payment.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </p>
                      {payment.line_items && payment.line_items.length > 0 && (
                        <ul className="mt-2 text-sm text-zinc-500">
                          {payment.line_items.map((item, idx) => (
                            <li key={idx} className="flex justify-between max-w-xs">
                              <span>{item.description}</span>
                              <span>
                                {payment.currency_mode === 'usd' 
                                  ? `$${(item.amount / 83).toFixed(0)}` 
                                  : `₹${item.amount?.toLocaleString()}`}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="flex items-center gap-3">
                          {/* Determine which currency to show based on payment's currency_mode and user's country */}
                          {/* If currency_mode is 'both', show user's preferred currency based on their country */}
                          {(() => {
                            const userPreferredCurrency = user?.country === 'international' ? 'usd' : 'inr';
                            const showINR = payment.currency_mode === 'inr' || 
                              (payment.currency_mode === 'both' && userPreferredCurrency === 'inr') ||
                              (!payment.currency_mode);
                            const showUSD = payment.currency_mode === 'usd' || 
                              (payment.currency_mode === 'both' && userPreferredCurrency === 'usd');
                            const showBoth = payment.currency_mode === 'both' && !user?.country;
                            
                            return (
                              <>
                                {(showINR || showBoth) && (
                                  <div>
                                    <p className="text-sm text-zinc-500">INR</p>
                                    <p className="text-xl font-bold">₹{payment.amount_inr?.toLocaleString()}</p>
                                    <p className="text-xs text-zinc-500">+ 18% GST = ₹{Math.round(payment.amount_inr * 1.18).toLocaleString()}</p>
                                  </div>
                                )}
                                {showBoth && <span className="text-zinc-600">|</span>}
                                {(showUSD || showBoth) && payment.amount_usd && (
                                  <div>
                                    <p className="text-sm text-zinc-500">USD</p>
                                    <p className="text-xl font-bold">${payment.amount_usd?.toLocaleString()}</p>
                                    <p className="text-xs text-zinc-500">No GST</p>
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                      
                      {/* Pay button - show only the relevant currency based on user's country */}
                      <div className="flex flex-col gap-2">
                        {(() => {
                          const userPreferredCurrency = user?.country === 'international' ? 'usd' : 'inr';
                          const showINRBtn = payment.currency_mode === 'inr' || 
                            (payment.currency_mode === 'both' && userPreferredCurrency === 'inr') ||
                            (!payment.currency_mode);
                          const showUSDBtn = (payment.currency_mode === 'usd' || 
                            (payment.currency_mode === 'both' && userPreferredCurrency === 'usd')) && 
                            payment.amount_usd;
                          
                          return (
                            <>
                              {showINRBtn && (
                                <button
                                  className="bg-[#C41E3A] hover:bg-[#A01830] text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                                  onClick={() => handlePayment(payment, 'INR')}
                                  data-testid={`pay-inr-btn-${payment.id}`}
                                >
                                  <CreditCard className="w-4 h-4" />
                                  Pay ₹{Math.round(payment.amount_inr * 1.18).toLocaleString()}
                                </button>
                              )}
                              {showUSDBtn && (
                                <button
                                  className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                                  onClick={() => handlePayment(payment, 'USD')}
                                  data-testid={`pay-usd-btn-${payment.id}`}
                                >
                                  <CreditCard className="w-4 h-4" />
                                  Pay ${payment.amount_usd?.toLocaleString()}
                                </button>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                  
                  {payment.valid_until && (
                    <p className="mt-3 text-xs text-amber-400 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Expires: {new Date(payment.valid_until).toLocaleDateString()}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Payment History Toggle */}
        {paymentHistory.length > 0 && (
          <div className="mb-8">
            <button
              onClick={() => setShowPaymentHistory(!showPaymentHistory)}
              className="text-sm text-zinc-400 hover:text-white flex items-center gap-2 mb-4"
              data-testid="toggle-payment-history"
            >
              <CreditCard className="w-4 h-4" />
              {showPaymentHistory ? 'Hide' : 'Show'} Payment History ({paymentHistory.length})
            </button>
            
            {showPaymentHistory && (
              <div className="space-y-3">
                {paymentHistory.map((order) => (
                  <div 
                    key={order.id}
                    className="bg-zinc-900/50 border border-zinc-800 p-4 rounded-lg flex items-center justify-between"
                    data-testid={`order-${order.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        order.status === 'paid' ? 'bg-green-500/20' : 'bg-zinc-700'
                      }`}>
                        {order.status === 'paid' ? (
                          <CheckCircle className="w-4 h-4 text-green-400" />
                        ) : (
                          <Clock className="w-4 h-4 text-zinc-400" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium">{order.product_type?.replace('_', ' ').toUpperCase() || 'Payment'}</p>
                        <p className="text-xs text-zinc-500">{new Date(order.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold">
                        {order.currency === 'USD' ? `$${order.amount / 100}` : `₹${order.amount / 100}`}
                      </p>
                      <p className={`text-xs ${order.status === 'paid' ? 'text-green-400' : 'text-zinc-500'}`}>
                        {order.status?.toUpperCase()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── My Profiles ────────────────────────────────────────── */}
        <MyProfiles />

        {/* Create Profile Section */}
        <div className="mb-8">
          <h2 className="text-xl font-bold mb-4">Create a Profile</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <Link 
              to="/profile/create"
              className="bg-zinc-900 border border-zinc-800 hover:border-zinc-700 p-6 rounded-lg transition-colors group"
              data-testid="create-company-profile"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#C41E3A]/20 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-[#C41E3A]" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold group-hover:text-[#C41E3A] transition-colors">Company Profile</h3>
                  <p className="text-sm text-zinc-500">Showcase your company&apos;s story, products, and vision</p>
                </div>
                <ExternalLink className="w-5 h-5 text-zinc-600 group-hover:text-[#C41E3A] transition-colors" />
              </div>
            </Link>
            
            <Link 
              to="/leadership/create"
              className="bg-zinc-900 border border-zinc-800 hover:border-zinc-700 p-6 rounded-lg transition-colors group"
              data-testid="create-leadership-profile"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center">
                  <User className="w-6 h-6 text-purple-400" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold group-hover:text-purple-400 transition-colors">Leadership Profile</h3>
                  <p className="text-sm text-zinc-500">Build your professional story and thought leadership</p>
                </div>
                <ExternalLink className="w-5 h-5 text-zinc-600 group-hover:text-purple-400 transition-colors" />
              </div>
            </Link>
          </div>
        </div>

        {/* ── Refer & Earn (low priority — placed after core sections) ── */}
        <ReferEarnCard />

        {/* Limit Warning */}
        {user.videos_count >= user.videos_limit && user.subscription_tier !== 'business' && (
          <div className="bg-[#C41E3A]/10 border border-[#C41E3A]/30 p-4 rounded-sm mb-8 flex items-center gap-4" data-testid="limit-warning">
            <AlertCircle className="w-6 h-6 text-[#C41E3A] flex-shrink-0" />
            <div>
              <p className="font-bold text-[#C41E3A]">Monthly limit reached</p>
              <p className="text-sm text-zinc-400">Upgrade your plan to record more videos this month.</p>
            </div>
            <Link to="/#pricing" className="ml-auto btn-primary rounded-sm text-sm whitespace-nowrap">
              Upgrade Now
            </Link>
          </div>
        )}

        {/* Videos List */}
        <div className="mb-4">
          <h2 className="text-xl font-bold">Your Videos</h2>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
          </div>
        ) : videos.length === 0 ? (
          <div className="bg-zinc-900 border border-zinc-800 p-12 text-center" data-testid="empty-videos">
            <Video className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <h3 className="text-lg font-bold mb-2">No videos yet</h3>
            <p className="text-zinc-500 mb-6">Record your first quick bite video to get started.</p>
            <Link to="/studio" className="btn-primary rounded-sm inline-flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Record Your First Video
            </Link>
          </div>
        ) : (
          <div className="grid gap-4" data-testid="videos-grid">
            {videos.map((video) => (
              <div 
                key={video.id}
                className="bg-zinc-900 border border-zinc-800 p-4 flex flex-col md:flex-row gap-4"
                data-testid={`video-item-${video.id}`}
              >
                {/* Thumbnail */}
                <div className="w-full md:w-48 aspect-video bg-black flex-shrink-0 relative">
                  <video 
                    src={`${API_URL}${video.video_url}`}
                    className="w-full h-full object-cover"
                    muted
                    onMouseEnter={(e) => e.target.play()}
                    onMouseLeave={(e) => { e.target.pause(); e.target.currentTime = 0; }}
                  />
                  <div className="absolute bottom-2 right-2 bg-black/70 px-2 py-1 text-xs font-mono">
                    {Math.floor(video.duration / 60)}:{(video.duration % 60).toString().padStart(2, '0')}
                  </div>
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-lg mb-1 truncate">{video.title}</h3>
                  {video.description && (
                    <p className="text-zinc-500 text-sm mb-2 line-clamp-2">{video.description}</p>
                  )}
                  <div className="flex items-center gap-4 text-sm text-zinc-500">
                    <span className="flex items-center gap-1">
                      <Eye className="w-4 h-4" />
                      {video.views} views
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDate(video.created_at)}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button 
                    onClick={() => copyShareLink(video.share_id)}
                    className="p-2 bg-zinc-800 hover:bg-zinc-700 transition-colors rounded-sm"
                    title="Copy share link"
                    data-testid={`copy-link-${video.id}`}
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <Link 
                    to={`/v/${video.share_id}`}
                    target="_blank"
                    className="p-2 bg-zinc-800 hover:bg-zinc-700 transition-colors rounded-sm"
                    title="View video"
                    data-testid={`view-video-${video.id}`}
                  >
                    <ExternalLink className="w-5 h-5" />
                  </Link>
                  <button 
                    onClick={() => handleDelete(video.id)}
                    disabled={deleting === video.id}
                    className="p-2 bg-zinc-800 hover:bg-red-900/50 hover:text-red-400 transition-colors rounded-sm disabled:opacity-50"
                    title="Delete video"
                    data-testid={`delete-video-${video.id}`}
                  >
                    {deleting === video.id ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Trash2 className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Account Settings Section */}
        <div className="mt-12 pt-8 border-t border-zinc-800">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Settings className="w-5 h-5 text-zinc-400" />
            Account Settings
          </h2>
          
          {/* Delete Account */}
          <div className="bg-red-900/10 border border-red-900/30 rounded-xl p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-red-400 font-semibold mb-2">Delete Account</h3>
                <p className="text-zinc-400 text-sm">
                  Permanently delete your account and all associated data. This action cannot be undone.
                  Your profiles, videos, and personal information will be removed.
                </p>
              </div>
              <button
                onClick={() => setShowDeleteModal(true)}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium whitespace-nowrap transition-colors"
                data-testid="delete-account-btn"
              >
                Delete Account
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-red-400">Delete Account</h3>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmation('');
                  setDeleteReason('');
                }}
                className="p-1 hover:bg-zinc-800 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-zinc-400" />
              </button>
            </div>
            
            <div className="bg-red-900/20 border border-red-900/30 rounded-lg p-4 mb-4">
              <p className="text-red-300 text-sm">
                <strong>Warning:</strong> This will permanently delete:
              </p>
              <ul className="text-red-300 text-sm mt-2 space-y-1">
                <li>• Your user account and login</li>
                <li>• All company profiles you created</li>
                <li>• All leadership profiles</li>
                <li>• All recorded videos</li>
                <li>• Booth bookings and history</li>
              </ul>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-zinc-400 text-sm mb-2">
                  Why are you leaving? (Optional)
                </label>
                <select
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                >
                  <option value="">Select a reason...</option>
                  <option value="Not using the service">Not using the service</option>
                  <option value="Found a better alternative">Found a better alternative</option>
                  <option value="Too expensive">Too expensive</option>
                  <option value="Privacy concerns">Privacy concerns</option>
                  <option value="Technical issues">Technical issues</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              
              <div>
                <label className="block text-zinc-400 text-sm mb-2">
                  Type <span className="text-red-400 font-mono">DELETE</span> to confirm
                </label>
                <input
                  type="text"
                  value={deleteConfirmation}
                  onChange={(e) => setDeleteConfirmation(e.target.value)}
                  placeholder="DELETE"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white font-mono"
                  data-testid="delete-confirmation-input"
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteConfirmation('');
                    setDeleteReason('');
                  }}
                  className="flex-1 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={deleteConfirmation !== 'DELETE' || deletingAccount}
                  className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-zinc-700 disabled:text-zinc-500 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                  data-testid="confirm-delete-btn"
                >
                  {deletingAccount ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    'Delete My Account'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
